<template>
    <layout-box :class="{ 'datatable-scroll-wrapper': scrollable }">
        <div class="datatable-scroll">
        <table class="datatable-table">
            <template v-if="showData && hasColumns">
                <thead class="datatable-table-thead">
                    <tr>
                        <th v-if="props.sortable" class="datatable-thead-th--sortable"></th>
                        <th
                            v-for="column in columns"
                            :key="column.slug"
                            scope="col"
                            :width="getWidth(column.width)"
                            class="datatable-thead-th"
                            :dusk="`column-${column.slug}`"
                            :class="[
                                {
                                    'cursor-pointer': column.sortable,
                                    'datatable-thead-th--sorted': isSortedBy(column.slug),
                                    'datatable-thead-th--not-sorted': !isSortedBy(column.slug),
                                    'datatable-sticky-left': column.sticky === 'left',
                                    'datatable-sticky-right': column.sticky === 'right',
                                    'datatable-highlight': column.highlight,
                                },
                                `datatable-thead-th-${column.slug}`,
                            ]"
                            :style="
                                column.sticky === 'right'
                                    ? { right: stickyRightOffsets[column.slug] + 'px' }
                                    : undefined
                            "
                            @click="sortBy(column)"
                        >
                            <slot :name="`heading-${column.slug}`">
                                {{ column.name }}
                            </slot>
                            <span
                                v-if="column.sortable && isSortedBy(column.slug)"
                                class="absolute -translate-y-1/2 right-2 top-1/2"
                            >
                                <icon
                                    v-if="params.way === 'desc'"
                                    icon="hero/outline/chevron-up"
                                    class="w-3 h-3"
                                ></icon>
                                <icon v-else icon="hero/outline/chevron-down" class="w-3 h-3"></icon>
                            </span>
                            <span
                                v-else-if="column.sortable"
                                class="absolute -translate-y-1/2 right-2 top-1/2 opacity-40 hover:opacity-80"
                            >
                                <icon icon="hero/outline/selector" class="w-3 h-3"></icon>
                            </span>
                        </th>
                    </tr>
                </thead>
                <Sortable
                    :list="filtered"
                    item-key="id"
                    tag="tbody"
                    class="datatable-tbody"
                    :options="props.sortableOptions"
                    @end="sorted"
                >
                    <template #item="{ element: entry }">
                        <tr
                            class="datatable-tr"
                            :class="getRowStyle(entry).class"
                            :style="getRowStyle(entry).style"
                            :data-id="entry.id ?? null"
                            :data-parent-id="entry.parent_id ?? null"
                            @click.exact="
                                props.clickable && props.clickable.row ? props.clickable.row(entry, $event) : null
                            "
                            @click.meta="pickSelect(entry['id'])"
                            @click.shift="pickSelect(entry['id'])"
                            @contextmenu="onRowContextMenu($event, entry)"
                        >
                            <td
                                v-if="props.sortable"
                                class="hidden select-none datatable-td--sortable cursor-grab lg:table-cell"
                            >
                                <icon
                                    icon="hero/outline/dots-vertical"
                                    class="w-4 h-4 mx-auto pointer-events-none"
                                ></icon>
                            </td>
                            <td
                                v-for="column in columns"
                                :key="column.slug"
                                class="relative block datatable-td lg:table-cell"
                                :class="[
                                    {
                                        'datatable-sticky-left': column.sticky === 'left',
                                        'datatable-sticky-right': column.sticky === 'right',
                                        'datatable-highlight': column.highlight,
                                    },
                                    `datatable-td-${column.slug}`,
                                ]"
                                :style="
                                    column.sticky === 'right'
                                        ? { right: stickyRightOffsets[column.slug] + 'px' }
                                        : undefined
                                "
                            >
                                <slot
                                    v-if="column.type === 'id'"
                                    :name="`cell-${column.slug}`"
                                    :entry="entry"
                                    :column="column.slug"
                                >
                                    <input
                                        v-model="selected"
                                        type="checkbox"
                                        :value="entry[column.slug]"
                                        class="hidden datatable-checkbox lg:inline-block"
                                        @click="pickSelect(entry[column.slug])"
                                    />
                                </slot>
                                <slot
                                    v-else-if="column.gridComponent"
                                    :name="`cell-${column.slug}`"
                                    :entry="entry"
                                    :column="column.slug"
                                >
                                    <component
                                        :is="column.gridComponent"
                                        :entry="entry"
                                        :column="column.slug"
                                        :value="entry[column.slug]"
                                    ></component>
                                </slot>
                                <slot
                                    v-else-if="resolveCellRenderer(column.renderer)"
                                    :name="`cell-${column.slug}`"
                                    :entry="entry"
                                    :column="column.slug"
                                >
                                    <component
                                        :is="resolveCellRenderer(column.renderer)"
                                        :entry="entry"
                                        :column="column"
                                        :value="entry[column.slug]"
                                        :options="column.rendererOptions ?? {}"
                                    ></component>
                                </slot>
                                <slot v-else :name="`cell-${column.slug}`" :entry="entry" :column="column.slug">
                                    <!-- eslint-disable -->
                                    <span v-html="readableValue(entry[column.slug], column)"></span>
                                    <!-- eslint-enable -->
                                </slot>
                            </td>
                        </tr>
                        <!-- After row slot for custom expansion content -->
                        <slot name="after-row" :entry="entry" :columns="columns"></slot>
                    </template>
                </Sortable>
            </template>
            <template v-else-if="showData && !hasColumns">
                <tbody>
                    <tr>
                        <td>
                            <IllustratedMessage>
                                <template #image>
                                    <icon icon="hero/outline/view-columns" class="w-12 h-12"></icon>
                                </template>
                                <template #title>{{ lang('general.datagrid.no_columns') }} </template>
                                {{ lang('general.datagrid.no_columns_description') }}
                            </IllustratedMessage>
                        </td>
                    </tr>
                </tbody>
            </template>
            <template v-else>
                <tbody>
                    <tr>
                        <td>
                            <IllustratedMessage>
                                <template #image>
                                    <icon icon="hero/outline/search" class="w-12 h-12"></icon>
                                </template>
                                <template #title>{{ lang('general.datagrid.no_entries') }} </template>
                                {{ lang('general.datagrid.no_entries_description') }}
                            </IllustratedMessage>
                        </td>
                    </tr>
                </tbody>
            </template>
        </table>
        </div>
    </layout-box>
</template>

<script setup lang="ts">
import { unref, computed, ref, watch } from 'vue';
import { ListingParams, Column, ColumnWidth, RowStyler } from './types';
import { lang } from '@/lang/composable';
import { arrayMove } from '@src/Common/Helpers/array';
import { getMaskedValue } from '@src/Common/Composables/fieldMask';
import { resolveCellRenderer } from './renderers';

interface Props {
    entries?: any;
    params: ListingParams;
    columns?: Column[] | null;
    selected?: number[];
    sortable?: boolean;
    scrollable?: boolean;
    sortableOptions?: object;
    rowStyler?: RowStyler;
    clickable?: {
        row: (row: any, $event: MouseEvent) => void;
    };
}

const emit = defineEmits<{
    (event: 'sort', column: string): void;
    (event: 'select', { id, isSelected }: { id: number; isSelected: boolean }): void;
    (event: 'selected', ids: any[]): void;
    (event: 'sorted', { ids }: { ids: number[] | string[] }): void;
    (event: 'row-contextmenu', { event, entryId }: { event: MouseEvent; entryId: number }): void;
    (event: 'row-context', { event, entry }: { event: MouseEvent; entry: any }): void;
}>();

const props = withDefaults(defineProps<Props>(), {
    entries: [],
    columns: null,
    selected: () => [],
    sortable: false,
    scrollable: false,
    sortableOptions: () => ({}),
    rowStyler: null,
    clickable: null,
});

const selected = ref(props.selected);

const readableValue = (value, column) => {
    if (!value) {
        return;
    }

    if (!column.type) {
        return value;
    }

    if (!column.mask) {
        return value;
    }

    if (Array.isArray(column.mask) && column.mask.length === 0) {
        return value;
    }

    return getMaskedValue({
        value,
        type: column.type,
        mask: column.mask,
    });
};

const stickyRightOffsets = computed(() => {
    const offsets: Record<string, number> = {};
    const rightStickyCols = columns.value.filter((col) => col.sticky === 'right').reverse();
    let offset = 0;
    for (const col of rightStickyCols) {
        offsets[col.slug] = offset;
        offset += typeof col.width === 'number' ? col.width : 0;
    }
    return offsets;
});

const filtered = computed(() => {
    return unref(props.entries);
});

const showData = computed(() => {
    return unref(props.entries).length > 0;
});

const hasColumns = computed(() => {
    return unref(props.columns).length > 0;
});

const columns = computed((): Column[] => {
    const entries = unref(props.entries);

    if (props.columns) {
        return props.columns;
    }

    const first = entries.find(() => true);

    if (!first) {
        return [];
    }

    return Object.keys(first).map((key) => ({ slug: key, name: key }));
});

const sortBy = (column: Column): void => {
    if (column.sortable) {
        emit('sort', column.slug);
    }
};

const isSortedBy = (column: string): boolean => {
    return props.params.sort === column;
};

const getWidth = (width: ColumnWidth) => {
    if (typeof width === 'undefined') {
        return 'auto';
    }

    if (width === 'auto') {
        return 'auto';
    }

    return width;
};

const getRowStyle = (entry: any) => {
    if (!props.rowStyler) {
        return {};
    }

    const style = props.rowStyler(entry);
    if (!style) {
        return {};
    }

    return {
        class: style.class || '',
        style: style.style || {},
    };
};

watch(
    props,
    (value) => {
        selected.value = Object.values(value.selected);
    },
    { deep: true },
);

watch(selected, (value, oldValue) => {
    if (value.length !== oldValue.length) {
        emit('selected', Object.values(value));
    }
});

const pickSelect = (id: number) => {
    const isSelected = Object.values(selected.value).includes(id);
    if (!isSelected) {
        selected.value.push(id);
    } else {
        selected.value = selected.value.filter((item) => item !== id);
    }

    emit('select', { id, isSelected: !isSelected });
    emit('selected', Object.values(selected.value));
};

const sorted = (event) => {
    const originalValue = unref(props.entries);
    const newValue = arrayMove(originalValue, event.oldIndex, event.newIndex);

    emit('sorted', { ids: newValue.map((item) => item.id) as number[] | string[] });
};

const onRowContextMenu = (event: MouseEvent, entry: any) => {
    // Always-emit channel for pages that own a per-row contextmenu (e.g. Disk Browser).
    // Fired before the selected-only `row-contextmenu` so the consumer can preventDefault.
    emit('row-context', { event, entry });

    if (selected.value.includes(entry.id)) {
        event.preventDefault();
        emit('row-contextmenu', { event, entryId: entry.id });
    }
};
</script>
